<template>
  <li :style="zoom">
    <div ref="player" class="player" :class="[
      {
        dead: props.player.isDead,
        marked: votingStore.markedPlayer === index,
        'no-vote': !props.player.voteToken,
        you:
          session.sessionId &&
          props.player.id &&
          props.player.id === session.playerId,
        'dont-vote': props.player.raisedHands === 0,
        'vote-once': props.player.raisedHands === 1,
        'vote-twice': props.player.raisedHands === 2,
        'hand-raised': !!props.player.handRaisedAt,
        'vote-lock': voteLocked,
        'pending-reveal': pendingReveal,
      },
      props.player.role?.team,
    ]">
      <div class="shroud" @click="toggleStatus()" />
      <div class="life" @click="toggleStatus()" />

      <div v-if="nightOrder.get(props.player).first && showOrderBubbles" class="night-order first">
        <em>{{ nightOrder.get(props.player).first }}</em>
        <span v-if="firstNightReminder(props.player)">
          {{ firstNightReminder(props.player) }}
        </span>
      </div>
      <div v-if="nightOrder.get(props.player).other && showOrderBubbles" class="night-order other">
        <em>{{ nightOrder.get(props.player).other }}</em>
        <span v-if="otherNightReminder(props.player)">
          {{ otherNightReminder(props.player) }}
        </span>
      </div>

      <Token :role="props.player.role" :player="props.player" :class="[{ 'transparent': pendingReveal }]"
        @set-role="$emit('trigger', ['openRoleModal'])" />

      <!-- Overlay icons -->
      <div class="overlay">
        <font-awesome-icon v-if="(grimoire.gamePhase !== 'firstNight' && grimoire.gamePhase !== 'otherNight') || !session.isPlayerOrSpectator || props.player.id === session.playerId" icon="hand-paper" class="fa fa-hand-paper hand" :title="t('player.releaseSpeak')"
          :data-order="handRaisedOrder"
          @click="lowerRaisedHand()"></font-awesome-icon>
        <span v-if="((grimoire.gamePhase !== 'firstNight' && grimoire.gamePhase !== 'otherNight') || !session.isPlayerOrSpectator) && !!handRaisedOrder" class="hand-order">{{ handRaisedOrder }}</span>
        <font-awesome-icon v-if="session.sessionId && session.isPlayerOrSpectator && !session.isSeated && !props.player.id"
          icon="chair" class="fa fa-chair chair" :title="t('menu.claimSeat')" @click="claimSeat()" />
        <font-awesome-icon v-if="
          !grimoire.isSecretVote ||
          isSpecialVoteWithMessages ||
          !session.isPlayerOrSpectator ||
          props.player.id === session.playerId
        " icon="hand-paper" class="fa fa-hand-paper vote simpleVote" :title="t('player.handUp')"
          @click="changeVote()" />
        <font-awesome-icon v-if="
          !grimoire.isSecretVote ||
          isSpecialVoteWithMessages ||
          !session.isPlayerOrSpectator ||
          props.player.id === session.playerId
        " icon="hand-paper" class="fa fa-hand-paper vote doubleVote leftHand" title="Double Vote 1"
          @click="changeVote()" />
        <font-awesome-icon v-if="
          !grimoire.isSecretVote ||
          isSpecialVoteWithMessages ||
          !session.isPlayerOrSpectator ||
          props.player.id === session.playerId
        " icon="hand-paper" class="fa fa-hand-paper vote doubleVote rightHand" title="Double Vote 2"
          @click="changeVote()" />
        <font-awesome-icon v-if="
          grimoire.isSecretVote &&
          !isSpecialVoteWithMessages &&
          session.isPlayerOrSpectator &&
          props.player.id !== session.playerId
        " icon="question" class="fa fa-question vote" :title="t('player.handUp')" @click="changeVote()" />
        <font-awesome-icon v-if="
          !grimoire.isSecretVote ||
          !session.isPlayerOrSpectator ||
          props.player.id === session.playerId
        " icon="times" class="fa fa-times vote" :title="t('player.handDown')" @click="changeVote()" />
        <font-awesome-icon v-if="
          grimoire.isSecretVote &&
          !isSpecialVoteWithMessages &&
          session.isPlayerOrSpectator &&
          props.player.id !== session.playerId
        " icon="question" class="fa fa-question vote" :title="t('player.handDown')" @click="changeVote()" />
        <font-awesome-icon icon="times-circle" class="fa fa-times-circle cancel" :title="t('player.cancel')"
          @click="cancel()" />
        <font-awesome-icon icon="exchange-alt" class="fa fa-exchange-alt swap" :title="t('player.swap')"
          @click="swapPlayer(props.player)" />
        <font-awesome-icon icon="redo-alt" class="fa fa-redo-alt move" :title="t('player.move')"
          @click="movePlayer(props.player)" />
        <font-awesome-icon icon="hand-point-right" class="fa fa-hand-point-right nominate" :title="t('player.nominate')"
          @click="nominatePlayer(props.player)" />
        <font-awesome-icon icon="hand-scissors" class="fa fa-hand-scissors rochambo"
          :title="t('player.rochamboWith')" @click="rochamboPlayer(props.player)" />
        <font-awesome-icon
          v-if="grimoire.gamePhase === 'postgame' && !session.isPlayerOrSpectator && props.player.secretRole && props.player.role?.id"
          icon="eye" class="fa fa-eye eye" @click="revealPlayerRole()" />
      </div>

      <!-- Claimed seat icon -->
      <font-awesome-icon v-if="props.player.id && session.sessionId" icon="chair" class="fa fa-chair seat"
        :class="{ highlight: session.isRolesDistributed }" />

      <!-- Ghost vote icon -->
      <font-awesome-icon v-if="
        (props.player.isDead || playersStore.hasFeature(player.role, 'need-token')) &&
        props.player.voteToken
      " icon="vote-yea" class="fa fa-vote-yea has-vote" :title="t('player.ghostVote')"
        @click="updatePlayer('voteToken', false)" />
      <font-awesome-icon v-if="
        (props.player.isDead || playersStore.hasFeature(player.role, 'need-token')) &&
        !props.player.voteToken &&
        !session.isPlayerOrSpectator
      " icon="vote-yea" class="fa fa-vote-yea has-vote no-token" :title="t('player.ghostVote')"
        @click="updatePlayer('voteToken', true)" />

      <!-- On block icon -->
      <div class="marked">
        <font-awesome-icon v-if="!(session.isPlayerOrSpectator && grimoire.isSecretVote)" icon="skull"
          class="fa fa-skull" />
      </div>
      <div class="name" :class="{ active: isMenuOpen }" @click="isMenuOpen = !isMenuOpen">
        <span>{{
          props.player.name
          }}</span>
        <font-awesome-icon v-if="props.player.pronouns" icon="venus-mars" class="fa fa-venus-mars" />
        <div v-if="props.player.pronouns" class="pronouns">
          <span>{{ props.player.pronouns }}</span>
        </div>
      </div>

      <transition name="fold">
        <ul v-if="isMenuOpen" class="menu">
          <li v-if="
            (session.isPlayerOrSpectator && player.id === session.playerId) ||
            (!session.isPlayerOrSpectator && !!player.handRaisedAt)
          " @click="toggleRaisedHand()">
            <font-awesome-icon icon="hand-paper" />
            {{ player.handRaisedAt ? t('player.releaseSpeak') : t('player.requestSpeak') }}
          </li>
          <li v-if="
            (!session.isPlayerOrSpectator && playersMenu.changePronouns) ||
            (session.isPlayerOrSpectator &&
              props.player.id === session.playerId)
          " @click="changePronouns">
            <font-awesome-icon icon="venus-mars" class="fa fa-venus-mars" />
            {{ t("player.changePronouns") }}
          </li>
          <li v-if="
            (!session.isPlayerOrSpectator && playersMenu.changeName) ||
            ((grimoire.allowSelfNaming || props.player.name === '') &&
              session.isPlayerOrSpectator &&
              player.id === session.playerId)
          " @click="changeName">
            <font-awesome-icon icon="user-edit" class="fa fa-user-edit" />
            {{ t("player.changeName") }}
          </li>
          <template v-if="!session.isPlayerOrSpectator">
            <li v-if="playersMenu.movePlayer" :class="{ disabled: votingStore.lockedVote }" @click="movePlayer()">
              <font-awesome-icon icon="redo-alt" class="fa fa-redo-alt" />
              {{ t("player.movePlayer") }}
            </li>
            <li v-if="playersMenu.swapPlayers" :class="{ disabled: votingStore.lockedVote }" @click="swapPlayer()">
              <font-awesome-icon icon="exchange-alt" class="fa fa-exchange-alt" />
              {{ t("player.swapPlayers") }}
            </li>
            <li v-if="playersMenu.removePlayer" :class="{ disabled: votingStore.lockedVote }" @click="removePlayer">
              <font-awesome-icon icon="times-circle" class="fa fa-times-circle" />
              {{ t("player.removePlayer") }}
            </li>
            <li v-if="
              props.player.id && session.sessionId && playersMenu.emptySeat
            " @click="updatePlayer('id', '', true)">
              <font-awesome-icon icon="chair" class="fa fa-chair" />
              {{ t("player.emptySeat") }}
            </li>
            <li v-if="
              props.player.role?.id &&
              (props.player.role?.team === 'traveler' ||
                playersMenu.swapAlignment)
            " @click="switchAlignment">
              <font-awesome-icon icon="yin-yang" class="fa fa-yin-yang" />
              {{ t("player.swapAlignment") }}
            </li>
            <li v-if="playersMenu.rochambo && !rochamboStore.match && grimoire.gamePhase === 'day'" @click="rochamboPlayer()">
              <font-awesome-icon icon="hand-scissors" class="fa fa-hand-scissors" />
              {{ t("player.rochambo") }}
            </li>
            <template v-if="!votingStore.nomination && playersMenu.specialVote && grimoire.gamePhase === 'day'">
              <li @click="specialVote()">
                <font-awesome-icon icon="vote-yea" class="fa fa-vote-yea" />
                {{ t("player.specialVote") }}
              </li>
            </template>
            <template v-if="!votingStore.nomination && grimoire.gamePhase === 'day'">
              <li @click="nominatePlayer()">
                <font-awesome-icon icon="hand-point-right" class="fa fa-hand-point-right" />
                {{ t("player.nomination") }}
              </li>
            </template>
            <li v-if="props.player.id && playersMenu.signal && (grimoire.gamePhase === 'firstNight' || grimoire.gamePhase === 'otherNight')" @click="signalPlayer()">
              <font-awesome-icon icon="tower-broadcast" class="fa fa-tower-broadcast" />
              {{ t("player.signal") }}
            </li>
          </template>
          <li v-if="session.isPlayerOrSpectator" :class="{
            disabled: props.player.id && props.player.id !== session.playerId,
          }" @click="claimSeat">
            <font-awesome-icon icon="chair" class="fa fa-chair" />
            <template v-if="!props.player.id">
              {{ t("player.claimSeat") }}
            </template>
            <template v-else-if="props.player.id === session.playerId">
              {{ t("player.vacateSeat") }}
            </template>
            <template v-else> {{ t("player.occupiedSeat") }}</template>
          </li>
        </ul>
      </transition>
    </div>

    <template v-if="props.player.reminders">
      <div v-for="reminder in props.player.reminders" :key="reminder.role?.id + ' ' + reminder.name" class="reminder"
        :class="[reminder.role?.id] + (reminder.special ? ' special' : '')" @click="removeReminder(reminder)">
        <picture v-if="reminder.role?.id !== 'custom'">
          <RoleIcon :role="reminder.role" />
        </picture>
        <span class="text">{{ reminder.name }}</span>
      </div>
    </template>
    <div class="reminder add" @click="$emit('trigger', ['openReminderModal'])">
      <span class="icon" />
    </div>
    <div class="reminderHoverTarget" />
  </li>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { RoleIcon, Token } from "@/components";
import { isActiveNomination } from "@/services/nomination";
import type { Player, Reminder, Role } from "@/types";
import {
  useChatStore,
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  usePlayersMenuStore,
  useRochamboStore,
  useSessionStore,
  useSignalsStore,
  useSoundboardStore,
  useUserPreferencesStore,
  useVotingStore,
  useDialogStore,
} from "@/stores";
import socket from "@/services/socket";

const locale = useLocaleStore();
const t = locale.t;
const dialogStore = useDialogStore();
const chatStore = useChatStore();
interface Props {
  player: Player;
  unchecked?: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update-player": [property: string, value: unknown];
  trigger: [action: string | [string, unknown] | [string]];
}>();

const grimoire = useGrimoireStore();
const playersStore = usePlayersStore();
const playersMenu = usePlayersMenuStore();
const rochamboStore = useRochamboStore();
const session = useSessionStore();
const signalsStore = useSignalsStore();
const userPreferences = useUserPreferencesStore();
const votingStore = useVotingStore();

const index = computed(() => players.value.indexOf(props.player));
const players = computed<Player[]>(() => playersStore.players);
const nightOrder = computed(() => playersStore.nightOrder);
const showOrderBubbles = computed(() => {
  if (session.isPlayerOrSpectator) {
    return userPreferences.orderBubblesAsPlayer;
  } else {
    return userPreferences.orderBubblesAsHost;
  }
});

const voteLocked = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return false;

  const nomination = votingStore.nomination;
  const playersCount = players.value.length;

  // Determine the reference player index for vote locking
  let referenceIndex: number;
  if (typeof nomination.nominee === "number") {
    referenceIndex = nomination.nominee;
  } else if (typeof nomination.nominator === "number") {
    referenceIndex = nomination.nominator;
  } else {
    return false; // Can't determine vote lock for special votes without player indices
  }

  const indexAdjusted =
    (index.value - 1 + playersCount - referenceIndex) % playersCount;
  return indexAdjusted < votingStore.lockedVote - 1;
});

const isSpecialVoteWithMessages = computed(() => {
  if (!isActiveNomination(votingStore.nomination)) return false;
  const nomination = votingStore.nomination;
  return !!nomination.specialVote?.timerText;
});

const zoom = computed(() => {
  if (players.value.length < 7) {
    return { width: 18 + userPreferences.zoom + "vmin" };
  } else if (players.value.length <= 10) {
    return { width: 16 + userPreferences.zoom + "vmin" };
  } else if (players.value.length <= 15) {
    return { width: 14 + userPreferences.zoom + "vmin" };
  } else {
    return { width: 12 + userPreferences.zoom + "vmin" };
  }
});

// Rank of this player in the queue of hands raised outside of an accusation, 0 when their hand is down
const handRaisedOrder = computed(() => {
  const raisedAt = props.player.handRaisedAt;
  if (!raisedAt) return 0;
  // Seat order breaks ties between hands raised within the same millisecond
  return players.value.filter(
    (p: Player, i: number) =>
      p.handRaisedAt &&
      (p.handRaisedAt < raisedAt ||
        (p.handRaisedAt === raisedAt && i <= index.value)),
  ).length;
});

const pendingReveal = computed(() => {
  if (grimoire.gamePhase === "postgame") {
    return props.player.secretRole;
  }
  return false;
});

const isMenuOpen = ref(false);

function firstNightReminder(player: Player) {
  if (!player.realRole?.impactOnRealNightOrder || player.realRole?.impactOnRealNightOrder === "no-change") {
    return player.role?.firstNightReminder;
  }
  if (player.realRole?.impactOnRealNightOrder === "replace") {
    return player.realRole?.firstNightReminder;
  }
  // From here, player.realRole?.impactOnRealNight order is "fill-blank"
  if (player.role?.firstNightReminder) {
    return player.role?.firstNightReminder;
  }
  return player.realRole?.firstNightReminder;
};

function otherNightReminder(player: Player) {
  if (!player.realRole?.impactOnRealNightOrder || player.realRole?.impactOnRealNightOrder === "no-change") {
    return player.role?.otherNightReminder;
  }
  if (player.realRole?.impactOnRealNightOrder === "replace") {
    return player.realRole?.otherNightReminder;
  }
  // From here, player.realRole?.impactOnRealNight order is "fill-blank"
  if (player.role?.otherNightReminder) {
    return player.role?.otherNightReminder;
  }
  return player.realRole?.otherNightReminder;
};

async function changePronouns() {
  if (session.isPlayerOrSpectator && props.player.id !== session.playerId)
    return;
  const pronouns = await dialogStore.prompt("Player pronouns", props.player.pronouns);
  if (pronouns !== null) {
    updatePlayer("pronouns", pronouns, true);
  }
}

function logDeath(isDead: boolean) {
  chatStore.logEvent(isDead ? "chat.log.died" : "chat.log.revived", {
    player: props.player.name,
  });
}

function toggleStatus() {
  const soundboard = useSoundboardStore();

  if (userPreferences.hideGrim) {
    if (!props.player.isDead) {
      updatePlayer("isDead", true);
      logDeath(true);
      soundboard.playSound({ sound: "death" });
    } else if (props.player.voteToken) {
      updatePlayer("voteToken", false);
    } else {
      updatePlayer("voteToken", true);
      updatePlayer("isDead", false);
      logDeath(false);
    }
  } else {
    updatePlayer("isDead", !props.player.isDead);
    logDeath(props.player.isDead);
    if (props.player.isDead) {
      soundboard.playSound({ sound: "death" });
    }
    if (props.player.voteToken !== props.player.isDead) {
      updatePlayer("voteToken", !props.player.voteToken);
    }
  }
}

function switchAlignment() {
  let selectedPlayer = players.value.find((player) => player === props.player);
  if (!selectedPlayer) return;
  if (
    selectedPlayer.alignment === undefined ||
    selectedPlayer.alignment === null
  ) {
    if (
      selectedPlayer.role?.team === "townsfolk" ||
      selectedPlayer.role?.team === "outsider"
    ) {
      selectedPlayer.alignment = "evil";
    } else {
      selectedPlayer.alignment = "good";
    }
  } else if (selectedPlayer.alignment === "good") {
    selectedPlayer.alignment = "evil";
  } else if (selectedPlayer.role?.team === "traveler") {
    selectedPlayer.alignment = null;
  } else {
    selectedPlayer.alignment = "good";
  }
}

async function changeName() {
  if (session.isPlayerOrSpectator && props.player.id !== session.playerId)
    return;
  const name =
    (await dialogStore.prompt(t("prompt.addPlayer"), props.player.name)) || props.player.name;
  if (name !== null && name !== "") {
    updatePlayer("name", name, true);
  }
}

function removeReminder(reminder: Reminder) {
  let reminders = [...props.player.reminders];
  reminders.splice(props.player.reminders.indexOf(reminder), 1);
  updatePlayer("reminders", reminders, true);
  if (session.isPlayerOrSpectator) return;
  reminders = [...props.player.reminders]; // Edited version
  let canVoteTwice = false;
  let voteValue = 1;
  let realRole = {} as Role;
  for (const reminderElement of reminders) {
    if (reminderElement?.special) {
      switch (reminderElement?.special?.name) {
        case "two-hands":
          canVoteTwice = true;
          break;
        case "multiplier":
          if (typeof reminderElement?.special?.value === "number") {
            voteValue *= reminderElement!.special!.value as number;
          }
          break;
        case "replace-character":
          realRole = reminderElement!.role;
          break;
      }
    }
  }
  playersStore.update({
    player: props.player,
    property: "canVoteTwice",
    value: canVoteTwice
  });
  playersStore.update({
    player: props.player,
    property: "voteValue",
    value: voteValue
  });
  if (realRole !== props.player.realRole) {
    playersStore.update({
      player: props.player,
      property: "realRole",
      value: realRole
    });
    playersStore.update({
      player: props.player,
      property: "secretRole",
      value: true
    });
  }
}

function toggleRaisedHand() {
  playersStore.toggleRaisedHandAt();
}

function lowerRaisedHand() {
  if (session.isPlayerOrSpectator && props.player.id !== session.playerId) return;
  updatePlayer("handRaisedAt", null, false);
}

function updatePlayer(
  property: keyof Player,
  value: unknown,
  closeMenu = false,
) {
  const allowedPlayerActions = ["reminders", "pronouns", "name", "id", "raisedHands", "handRaisedAt"];
  if (
    session.isPlayerOrSpectator &&
    !allowedPlayerActions.includes(property)
  )
    return;
  playersStore.update({
    player: props.player,
    property,
    value,
  });
  if (closeMenu) {
    isMenuOpen.value = false;
  }
}

function removePlayer() {
  isMenuOpen.value = false;
  emit("trigger", ["removePlayer"]);
}

function swapPlayer(player?: Player) {
  isMenuOpen.value = false;
  emit("trigger", ["swapPlayer", player]);
}

function movePlayer(player?: Player) {
  isMenuOpen.value = false;
  emit("trigger", ["movePlayer", player]);
}

function nominatePlayer(player?: Player) {
  isMenuOpen.value = false;
  emit("trigger", ["nominatePlayer", player]);
}

function rochamboPlayer(player?: Player) {
  isMenuOpen.value = false;
  emit("trigger", ["rochamboPlayer", player]);
}

function specialVote() {
  isMenuOpen.value = false;
  votingStore.setPlayerForSpecialVote(players.value.indexOf(props.player));
  grimoire.toggleModal("specialVote");
}

function signalPlayer() {
  isMenuOpen.value = false;
  signalsStore.openFor(props.player.id);
}

function cancel() {
  emit("trigger", ["cancel"]);
}

function claimSeat() {
  isMenuOpen.value = false;
  emit("trigger", ["claimSeat"]);
  if (props.player.name === "") {
    setTimeout(() => {
      changeName();
    }, 100);
  }
}

function changeVote() {
  if (session.isPlayerOrSpectator) return;
  if (!voteLocked.value) return;
  let newVote;
  switch (props.player.raisedHands) {
    case 0:
      newVote = 1;
      break;
    case 1:
      if (props.player.canVoteTwice) {
        newVote = 2;
      }
      else {
        newVote = 0;
      }
      break;
    default:
      newVote = 0;
  }
  votingStore.voteSync([index.value, newVote]);
  playersStore.update({
    player: props.player,
    property: "raisedHands",
    value: newVote
  });
}

function revealPlayerRole() {
  updatePlayer("secretRole", false);
  setTimeout(() => {
    socket.send("player", {
      index: index.value,
      property: "alignment",
      value: props.player.alignment,
    });
    socket.send("player", {
      index: index.value,
      property: "role",
      value: props.player.realRole?.id ? props.player.realRole : props.player.role,
    });
  }, 500);
}
</script>

<style lang="scss" src="./Seat.scss" scoped></style>
